import {
  Badge,
  Box,
  Checkbox,
  Flex,
  Heading,
  Input,
  Spinner,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FiArrowLeft,
  FiEdit2,
  FiPlus,
  FiShield,
  FiTrash2,
} from "react-icons/fi"

import { OpenAPI } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { useOrganization } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/admin-dpp/representatives")({
  component: AdminDppRepresentatives,
})

interface RepresentativeRow {
  id: string
  organisation_id: string
  name: string
  address?: string | null
  email?: string | null
  phone?: string | null
  website?: string | null
  is_default?: boolean
  enabled?: boolean
}

function authedFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  })
}

function AdminDppRepresentatives() {
  const sc = useSemanticColors()
  const { t } = useTranslation("dpp")
  const navigate = useNavigate()
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  const { showToast } = useCustomToast()
  const orgId = currentOrganization?.id

  const listQuery = useQuery({
    queryKey: ["representatives", orgId],
    queryFn: async (): Promise<{
      data: RepresentativeRow[]
      count: number
    }> => {
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/representatives/?limit=200`,
      )
      if (!r.ok) throw new Error(await r.text())
      return r.json()
    },
    enabled: !!orgId,
    staleTime: 30_000,
  })

  const rows = listQuery.data?.data ?? []

  const [isOpen, setIsOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState({
    name: "",
    address: "",
    email: "",
    phone: "",
    website: "",
  })

  const resetDraft = () => {
    setEditingId(null)
    setDraft({ name: "", address: "", email: "", phone: "", website: "" })
  }
  const seedFromRow = (row: RepresentativeRow) => {
    setEditingId(row.id)
    setDraft({
      name: row.name ?? "",
      address: row.address ?? "",
      email: row.email ?? "",
      phone: row.phone ?? "",
      website: row.website ?? "",
    })
    setIsOpen(true)
  }

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const allSelected = rows.length > 0 && selectedIds.size === rows.length
  const toggleSelection = (id: string, next: boolean) =>
    setSelectedIds((prev) => {
      const out = new Set(prev)
      if (next) out.add(id)
      else out.delete(id)
      return out
    })
  const toggleAll = (next: boolean) => {
    if (!next) setSelectedIds(new Set())
    else setSelectedIds(new Set(rows.map((r) => r.id)))
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("No organisation selected")
      const body = {
        organisation_id: orgId,
        name: draft.name.trim(),
        address: draft.address.trim() || null,
        email: draft.email.trim() || null,
        phone: draft.phone.trim() || null,
        website: draft.website.trim() || null,
      }
      const url = editingId
        ? `/api/v1/organizations/${orgId}/representatives/${editingId}`
        : `/api/v1/organizations/${orgId}/representatives/`
      const res = await authedFetch(url, {
        method: editingId ? "PUT" : "POST",
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["representatives", orgId] })
      showToast(
        editingId
          ? t(
              "representatives.toast.updatedTitle",
              "Representative updated",
            )
          : t(
              "representatives.toast.createdTitle",
              "Representative created",
            ),
        t("representatives.toast.savedBody", "Saved."),
        "success",
      )
      setIsOpen(false)
      resetDraft()
    },
    onError: (err: unknown) => {
      showToast(
        t(
          "representatives.toast.errorTitle",
          "Could not save representative",
        ),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      if (!orgId) throw new Error("No organisation selected")
      const results = await Promise.allSettled(
        ids.map((id) =>
          authedFetch(
            `/api/v1/organizations/${orgId}/representatives/${id}`,
            { method: "DELETE" },
          ).then(async (res) => {
            if (!res.ok) throw new Error(await res.text())
          }),
        ),
      )
      const failed = results.filter((r) => r.status === "rejected")
      if (failed.length > 0) {
        const detail = (failed[0] as PromiseRejectedResult).reason
        throw new Error(
          detail instanceof Error ? detail.message : String(detail),
        )
      }
    },
    onSuccess: (_unused, ids) => {
      queryClient.invalidateQueries({ queryKey: ["representatives", orgId] })
      setSelectedIds(new Set())
      if (editingId && ids.includes(editingId)) {
        setIsOpen(false)
        resetDraft()
      }
      showToast(
        t("representatives.toast.deletedTitle", "Representative deleted"),
        t("representatives.toast.deletedBody", "{{count}} removed.", {
          count: ids.length,
        }),
        "success",
      )
    },
    onError: (err: unknown) => {
      showToast(
        t("representatives.toast.deleteErrorTitle", "Could not delete"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  const canSubmit = draft.name.trim().length > 0

  return (
    <Box>
      <Flex mb={4} align="center" gap={3}>
        <VisionButton
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/admin-dpp" })}
        >
          <FiArrowLeft />
          <Text ml={2}>{t("common.backToOverview")}</Text>
        </VisionButton>
      </Flex>

      <Flex align="center" gap={3} mb={6} flexWrap="wrap">
        <IconBox variant="glass" size="lg">
          <FiShield fontSize="24px" color={sc.iconColor} />
        </IconBox>
        <Box flex="1">
          <Heading size="lg" color={sc.heading}>
            {t("representatives.title", "Authorised Representatives")}
          </Heading>
          <Text color={sc.textMuted}>
            {t(
              "representatives.subtitle",
              "EU-mandated authorised representative contact blocks — linked from each DPP record.",
            )}
          </Text>
        </Box>
      </Flex>

      <VisionCard>
        <Flex
          justify="space-between"
          align="center"
          p={4}
          gap={3}
          flexWrap="wrap"
        >
          <Heading size="md" color={sc.heading}>
            {t("representatives.listTitle", "{{count}} total", {
              count: listQuery.data?.count ?? 0,
            })}
          </Heading>
          <Flex gap={2}>
            {selectedIds.size > 0 && (
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={deleteMutation.isPending}
                onClick={() =>
                  deleteMutation.mutate(Array.from(selectedIds))
                }
              >
                <FiTrash2 />
                <Text ml={1}>
                  {t("representatives.deleteSelected", "Delete {{count}}", {
                    count: selectedIds.size,
                  })}
                </Text>
              </VisionButton>
            )}
            <VisionButton
              variant="brand"
              size="sm"
              onClick={() => {
                resetDraft()
                setIsOpen(true)
              }}
            >
              <FiPlus />
              <Text ml={1}>
                {t("representatives.add", "Add representative")}
              </Text>
            </VisionButton>
          </Flex>
        </Flex>

        {listQuery.isLoading ? (
          <Flex justify="center" py={6}>
            <Spinner size="md" color={sc.spinnerColor} />
          </Flex>
        ) : rows.length === 0 ? (
          <Text color={sc.textMuted} fontSize="sm" px={4} pb={4}>
            {t("representatives.empty", "No representatives yet.")}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader w="40px">
                    <Checkbox.Root
                      checked={allSelected}
                      onCheckedChange={(d) => toggleAll(!!d.checked)}
                      size="sm"
                      aria-label="Select all"
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                    </Checkbox.Root>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("representatives.col.name", "Name")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("representatives.col.address", "Address")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("representatives.col.email", "Email")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("representatives.col.website", "Website")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader w="40px" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row) => {
                  const isSelected = selectedIds.has(row.id)
                  return (
                    <Table.Row key={row.id}>
                      <Table.Cell>
                        <Checkbox.Root
                          checked={isSelected}
                          onCheckedChange={(d) =>
                            toggleSelection(row.id, !!d.checked)
                          }
                          size="sm"
                          aria-label={`Select ${row.name}`}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                        </Checkbox.Root>
                      </Table.Cell>
                      <Table.Cell color={sc.heading} fontSize="sm">
                        <Flex gap={2} align="center" flexWrap="wrap">
                          <Text fontWeight="600">{row.name}</Text>
                          {row.is_default && (
                            <Badge colorPalette="blue" size="sm">
                              {t("representatives.default", "default")}
                            </Badge>
                          )}
                        </Flex>
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted} fontSize="sm">
                        {row.address ?? "—"}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted} fontSize="sm">
                        {row.email ?? "—"}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted} fontSize="sm">
                        {row.website ?? "—"}
                      </Table.Cell>
                      <Table.Cell>
                        <VisionButton
                          variant="ghost"
                          size="xs"
                          onClick={() => seedFromRow(row)}
                        >
                          <FiEdit2 />
                        </VisionButton>
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VisionCard>

      <DialogRoot
        open={isOpen}
        onOpenChange={(d) => {
          if (!d.open) resetDraft()
          setIsOpen(d.open)
        }}
        size="md"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? t(
                    "representatives.dialog.editTitle",
                    "Edit representative",
                  )
                : t(
                    "representatives.dialog.createTitle",
                    "Add representative",
                  )}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap={3}>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("representatives.field.name", "Name")}
                </Text>
                <Input
                  value={draft.name}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, name: e.target.value }))
                  }
                  placeholder="EU Compliance Partners SARL"
                  maxLength={200}
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("representatives.field.address", "Address")}
                </Text>
                <Input
                  value={draft.address}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, address: e.target.value }))
                  }
                  placeholder="Rue de la Loi 1, 1040 Brussels, Belgium"
                  maxLength={500}
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("representatives.field.email", "Email")}
                </Text>
                <Input
                  value={draft.email}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, email: e.target.value }))
                  }
                  placeholder="contact@example.eu"
                  maxLength={320}
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("representatives.field.phone", "Phone")}
                </Text>
                <Input
                  value={draft.phone}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, phone: e.target.value }))
                  }
                  placeholder="+32 2 555 1234"
                  maxLength={64}
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("representatives.field.website", "Website")}
                </Text>
                <Input
                  value={draft.website}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, website: e.target.value }))
                  }
                  placeholder="https://example.eu"
                  maxLength={500}
                />
              </Box>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Flex gap={2} w="100%" justify="space-between">
              <Box>
                {editingId && (
                  <VisionButton
                    variant="ghost"
                    size="sm"
                    colorPalette="red"
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate([editingId])}
                  >
                    <FiTrash2 />
                    <Text ml={1}>
                      {t("representatives.dialog.delete", "Delete")}
                    </Text>
                  </VisionButton>
                )}
              </Box>
              <Flex gap={2}>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsOpen(false)
                    resetDraft()
                  }}
                >
                  {t("representatives.dialog.cancel", "Cancel")}
                </VisionButton>
                <VisionButton
                  variant="brand"
                  size="sm"
                  disabled={!canSubmit || saveMutation.isPending}
                  onClick={() => saveMutation.mutate()}
                >
                  {t("representatives.dialog.save", "Save")}
                </VisionButton>
              </Flex>
            </Flex>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Box>
  )
}
